import React, { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import {
  AccessManagementAbsoluteRoutes,
  AiGrcQaAbsoluteRoutes,
  ControlsAbsoluteRoutes,
  DirectRoutes,
  EvidenceAbsoluteRoutes,
  ExamineAndTestAbsoluteRoutes,
  FrameworksAbsoluteRoutes,
  PoliciesAbsoluteRoutes,
  ReceptorsAbsoluteRoutes,
  RisksAbsoluteRoutes,
  RequestsAbsoluteRoutes,
  RoadmapAbsoluteRoutes,
  SchedulingAbsoluteRoutes,
  ServicesAbsoluteRoutes,
  TrustPortalConfigurationAbsoluteRoutes,
  TrusteroIntelligenceAbsoluteRoutes,
  VendorsAbsoluteRoutes,
} from 'src/components/Reusable/RootPage/RootPage.constants'
import NavIcons from 'src/components/Icons/NavIcons'
import { EroDarkEastSVG } from '../components/Icons/Brand'

type IndexPageTitleIconItem = {
  title: string
  routePrefixes?: string[]
  icon: JSX.Element
}

const normalizeRouteForMatch = (route: string): string =>
  route.replace(/^\//, '').split('?')[0]

const normalizePathnameForMatch = (pathname: string): string =>
  pathname.replace(/^\/+/, '')

const INDEX_TITLE_ICON_ALIASES: Record<string, string> = {
  'ai grc q&a': 'grc questions',
  'audit bot': 'control test',
  account: 'account settings',
  usage: 'ai usage',
  'trustero ai usage': 'ai usage',
  'manage documents': 'manage grc documents',
  indexing: 'indexing status',
  'compliance roadmap': 'roadmap',
}

const INDEX_PAGE_TITLE_ICONS: IndexPageTitleIconItem[] = [
  {
    title: 'Trustero Intelligence',
    routePrefixes: [TrusteroIntelligenceAbsoluteRoutes.INDEX],
    icon: <EroDarkEastSVG />,
  },
  {
    title: 'GRC Dashboards',
    routePrefixes: [DirectRoutes.GRC_DASHBOARDS],
    icon: <NavIcons.NavIconsDashboardSVG />,
  },
  {
    title: 'Dashboard',
    routePrefixes: [DirectRoutes.DASHBOARD],
    icon: <NavIcons.NavIconsDashboardSVG />,
  },
  {
    title: 'Reports',
    routePrefixes: [DirectRoutes.REPORTS],
    icon: <NavIcons.NavIconsReportsSVG />,
  },
  {
    title: 'GRC Questions',
    routePrefixes: [AiGrcQaAbsoluteRoutes.INDEX],
    icon: <NavIcons.NavIconsGrcQuestionsSVG />,
  },
  {
    title: 'Control Test',
    routePrefixes: [ExamineAndTestAbsoluteRoutes.INDEX_PAGE],
    icon: <NavIcons.NavIconsControlTestSVG />,
  },
  {
    title: 'Company Info',
    routePrefixes: ['company_info'],
    icon: <NavIcons.NavIconsCompanyInfoSVG />,
  },
  {
    title: 'Scope',
    routePrefixes: [DirectRoutes.SCOPE],
    icon: <NavIcons.NavIconsScopeSVG />,
  },
  {
    title: 'Help',
    routePrefixes: ['help/quickstart', 'help'],
    icon: <NavIcons.NavIconsHelpSVG />,
  },
  {
    title: 'Roadmap',
    routePrefixes: [RoadmapAbsoluteRoutes.INDEX],
    icon: <NavIcons.NavIconsRoadmapSVG />,
  },
  {
    title: 'Policies',
    routePrefixes: [PoliciesAbsoluteRoutes.INDEX_PAGE],
    icon: <NavIcons.NavIconsPoliciesSVG />,
  },
  {
    title: 'Controls',
    routePrefixes: [ControlsAbsoluteRoutes.INDEX_PAGE],
    icon: <NavIcons.NavIconsControlsSVG className="controls-nav-icon" />,
  },
  {
    title: 'Frameworks',
    routePrefixes: [FrameworksAbsoluteRoutes.INDEX],
    icon: <NavIcons.NavIconsFrameworksSVG />,
  },
  {
    title: 'Evidence',
    routePrefixes: [EvidenceAbsoluteRoutes.INDEX_PAGE],
    icon: <NavIcons.NavIconsEvidencesSVG />,
  },
  {
    title: 'Requests',
    routePrefixes: [RequestsAbsoluteRoutes.INDEX],
    icon: <NavIcons.NavIconsRequestsSVG />,
  },
  {
    title: 'Access Management',
    routePrefixes: [AccessManagementAbsoluteRoutes.INDEX],
    icon: <NavIcons.NavIconsAccessManagementSVG />,
  },
  {
    title: 'Content Management',
    routePrefixes: [TrustPortalConfigurationAbsoluteRoutes.ROOT],
    icon: <NavIcons.NavIconsContentManagementSVG />,
  },
  {
    title: 'Audits',
    routePrefixes: [DirectRoutes.AUDITS],
    icon: <NavIcons.NavIconsAuditsSVG />,
  },
  {
    title: 'Trust Portal',
    routePrefixes: [TrustPortalConfigurationAbsoluteRoutes.ROOT],
    icon: <NavIcons.NavIconsTrustPortalSVG />,
  },
  {
    title: 'Risks',
    routePrefixes: [RisksAbsoluteRoutes.INDEX],
    icon: <NavIcons.NavIconsRiskRegisterSVG />,
  },
  {
    title: 'Vendors',
    routePrefixes: [VendorsAbsoluteRoutes.INDEX],
    icon: <NavIcons.NavIconsVendorManagementSVG />,
  },
  {
    title: 'Integrations List',
    routePrefixes: [DirectRoutes.INTEGRATIONS_LIST],
    icon: <NavIcons.NavIconsIntegrationListSVG />,
  },
  {
    title: 'Receptors',
    routePrefixes: [ReceptorsAbsoluteRoutes.INDEX],
    icon: <NavIcons.NavIconsReceptorsSVG />,
  },
  {
    title: 'Services',
    routePrefixes: [ServicesAbsoluteRoutes.INDEX],
    icon: <NavIcons.NavIconsServicesSVG />,
  },
  {
    title: 'GRC Platforms',
    routePrefixes: [DirectRoutes.GRC_PLATFORMS],
    icon: <NavIcons.NavIconsGrcPlatformsSVG />,
  },
  {
    title: 'Document Management',
    routePrefixes: [DirectRoutes.DOCUMENT_MANAGEMENT],
    icon: <NavIcons.NavIconsDocumentManagementSVG />,
  },
  {
    title: 'Workflow Management',
    routePrefixes: [DirectRoutes.WORKFLOW_MANAGEMENT],
    icon: <NavIcons.NavIconsWorkflowManagementSVG />,
  },
  {
    title: 'Users',
    routePrefixes: [DirectRoutes.USERS],
    icon: <NavIcons.NavIconsUsersSVG />,
  },
  {
    title: 'Account Settings',
    routePrefixes: [DirectRoutes.ACCOUNT_SETTINGS],
    icon: <NavIcons.NavIconsAccountsSVG />,
  },
  {
    title: 'Your Preferences',
    routePrefixes: [DirectRoutes.YOUR_PREFERENCE],
    icon: <NavIcons.NavIconsPreferenceSVG />,
  },
  {
    title: 'Activity Log',
    icon: <NavIcons.NavIconsActivityLogSVG />,
  },
  {
    title: 'Switch Accounts',
    routePrefixes: [DirectRoutes.ACCOUNTS],
    icon: <NavIcons.NavIconsSwitchAccountSVG />,
  },
  {
    title: 'Prompt Overrides',
    routePrefixes: [DirectRoutes.PROMPT_OVERRIDES],
    icon: <NavIcons.NavIconsPromptOverridesSVG />,
  },
  {
    title: 'Internal Account Management',
    routePrefixes: [DirectRoutes.INTERNAL_ACCOUNT_MANAGEMENT],
    icon: <NavIcons.NavIconsAccountsSVG />,
  },
  {
    title: 'AI Usage',
    routePrefixes: [DirectRoutes.USAGE],
    icon: <NavIcons.NavIconsAiUsageSVG />,
  },
  {
    title: 'Manage GRC Documents',
    routePrefixes: [DirectRoutes.MANAGE_DOCUMENTS],
    icon: <NavIcons.NavIconsManageDocumentsSVG />,
  },
  {
    title: 'Scheduling',
    routePrefixes: [SchedulingAbsoluteRoutes.INDEX],
    icon: <NavIcons.NavIconsSchedulingSVG />,
  },
  {
    title: 'Indexing Status',
    routePrefixes: [DirectRoutes.INDEXING],
    icon: <NavIcons.NavIconsIndexingSVG />,
  },
]

export const getIndexPageTitleIcon = (
  pathname: string,
  title?: string,
): JSX.Element | null => {
  if (title) {
    const normalizedTitle = title.trim().toLowerCase()
    const lookupTitle =
      INDEX_TITLE_ICON_ALIASES[normalizedTitle] ?? normalizedTitle
    const matchingTitleItem = INDEX_PAGE_TITLE_ICONS.find(
      (item) => item.title.trim().toLowerCase() === lookupTitle,
    )

    if (matchingTitleItem) {
      return matchingTitleItem.icon
    }
  }

  const normalizedPathname = normalizePathnameForMatch(pathname)

  const matchingRouteItem = INDEX_PAGE_TITLE_ICONS.find((item) =>
    item.routePrefixes?.some((routePrefix) => {
      const normalizedRoute = normalizeRouteForMatch(routePrefix)
      return (
        normalizedPathname === normalizedRoute ||
        normalizedPathname.startsWith(`${normalizedRoute}/`) ||
        normalizedPathname.endsWith(`/${normalizedRoute}`) ||
        normalizedPathname.includes(`/${normalizedRoute}/`)
      )
    }),
  )

  return matchingRouteItem?.icon ?? null
}

export const useIndexPageTitleIcon = (title?: string): JSX.Element | null => {
  const location = useLocation()

  return useMemo(
    () => getIndexPageTitleIcon(location.pathname, title),
    [location.pathname, title],
  )
}
